import { useQuery } from '@tanstack/react-query';
import type { Chapter, Lesson, Competence } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

export type AdminChapterSummary = Chapter & {
  lesson_count: number;
  competence_count: number;
};

export type ImportLogEntry = { ts: string; level: 'info' | 'warn' | 'error'; message: string };
export type ImportLog = {
  started_at: string;
  finished_at?: string;
  status: 'running' | 'success' | 'error';
  entries: ImportLogEntry[];
};

export type AdminChapterDetail = Chapter & {
  lessons: Lesson[];
  competences: Competence[];
  last_import_log: ImportLog | null;
};

export function useAdminChapters() {
  return useQuery({
    queryKey: ['admin-chapters'],
    queryFn: async (): Promise<AdminChapterSummary[]> => {
      const { data, error } = await supabase
        .from('chapters')
        .select('*, lessons(count), competences(count)')
        .order('subject')
        .order('display_order');
      if (error) throw new Error(error.message);
      return (data ?? []).map((row: any) => ({
        ...row,
        lesson_count: row.lessons?.[0]?.count ?? 0,
        competence_count: row.competences?.[0]?.count ?? 0,
      }));
    },
  });
}

export function useAdminChapterDetail(chapterId: string) {
  return useQuery({
    queryKey: ['admin-chapter-detail', chapterId],
    queryFn: async (): Promise<AdminChapterDetail | null> => {
      const { data, error } = await supabase
        .from('chapters')
        .select('*, lessons(*), competences(*)')
        .eq('id', chapterId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const { lessons, competences, ...chapter } = data as any;
      return {
        ...chapter,
        lessons: (lessons ?? []).sort((a: Lesson, b: Lesson) => a.display_order - b.display_order),
        competences: (competences ?? []).sort(
          (a: Competence, b: Competence) => a.display_order - b.display_order,
        ),
      };
    },
    enabled: !!chapterId,
  });
}

export type AdminLessonDetail = Lesson & {
  chapter: { id: string; title: string; subject: string; level: string } | null;
};

export function useAdminLessonDetail(lessonId: string) {
  return useQuery({
    queryKey: ['admin-lesson-detail', lessonId],
    queryFn: async (): Promise<AdminLessonDetail | null> => {
      const { data, error } = await supabase
        .from('lessons')
        .select('*, chapters(id, title, subject, level)')
        .eq('id', lessonId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!data) return null;
      const { chapters, ...rest } = data as unknown as Lesson & {
        chapters: { id: string; title: string; subject: string; level: string } | null;
      };
      return { ...rest, chapter: chapters ?? null };
    },
    enabled: !!lessonId,
  });
}

export function useAdminCompetenceDetail(competenceId: string) {
  return useQuery({
    queryKey: ['admin-competence-detail', competenceId],
    queryFn: async (): Promise<Competence | null> => {
      const { data, error } = await supabase
        .from('competences')
        .select('*')
        .eq('id', competenceId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data ?? null;
    },
    enabled: !!competenceId,
  });
}
